
import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "../../HOC/axiosClient";
import config from "../../Config/applicationEnv";
import { Button, message } from "antd";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import "./EmailRecord.css";

const EmailRecord = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [colDefs, setColDefs] = useState([
    { field: "S.No", valueGetter: "node.rowIndex + 1" },
    { field: "user_name", headerName: "User Name" },
    { field: "receiver_email", headerName: "Receiver Email" },
    { field: "cc", headerName: "CC" },
    { field: "body_preview", headerName: "Body" },
    { field: "subject", headerName: "Subject" },
    {
      field: "sent_at",
      headerName: "Sent At",
      valueGetter: (params) =>
        params?.data.sent_at
          ? new Date(params.data.sent_at).toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
            })
          : "",
    },
    {
      field: "success",
      headerName: "Success",
      valueGetter: (params) => (params.data.success ? "Success" : "Failed"),
    },
  ]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [userList, setUserList] = useState([]);
  const [selectedFilterUserId, setSelectedFilterUserId] = useState("");

  const token = JSON.parse(localStorage.getItem("slotbook"));
  const id = token?.userId;
  const accessToken = token?.accessToken;
  const isAdmin = token?.roleId != null && String(token.roleId) === "1";

  useEffect(() => {
    if (!isAdmin) return;
    const fetchUsers = async () => {
      try {
        const res = await axios.get(
          `${config.BASE_URL}exam_sch/api/user_table/create?page_size=200`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const list = res.data?.results || res.data || [];
        setUserList(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Failed to load user list", e);
      }
    };
    fetchUsers();
  }, [isAdmin, accessToken]);

  const getAdminTable = async (pageNumber) => {
    setLoading(true);
    try {
      message.info("Loading report...");
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      const day = selectedDate.getDate();
      let url = `${config.BASE_URL}exam_sch/api/search-emails/?month=${month}&year=${year}&day=${day}&page=${pageNumber}`;

      if (!isAdmin) {
        url += `&user_id=${id}`;
      } else if (selectedFilterUserId) {
        url += `&user_id=${selectedFilterUserId}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const totalRecords = response.data.count || 0;
      const recordsPerPage = response.data.results?.length || 0;
      const totalPageCount = Math.max(1, Math.ceil(totalRecords / recordsPerPage) || 1);

      const startingSerialNumber = (pageNumber - 1) * recordsPerPage + 1;

      const updatedRowData = (response.data.results || []).map((row, index) => ({
        ...row,
        serialNumber: startingSerialNumber + index,
      }));

      setRowData(updatedRowData);
      setTotalPageCount(totalPageCount);
      setLoading(false);
      message.success("Loading success");
    } catch (error) {
      setLoading(false);
      console.error("Error loading email records:", error);
      message.error("Failed to load email records. Please try again later.");
    }
  };

  useEffect(() => {
    getAdminTable(currentPage);
  }, [currentPage, selectedDate, selectedFilterUserId]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setCurrentPage(1);
  };

  const renderPageNumbers = () => {
    const range = 2;
    const start = Math.max(1, currentPage - range);
    const end = Math.min(totalPageCount, currentPage + range);
    const pages = [];
    for (let i = start; i <= end; i++) {
      const isCurrentPage = i === currentPage;
      pages.push(
        <button
          type="button"
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`email-record-page-btn ${isCurrentPage ? "active" : ""}`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const downloadFailedData = () => {
    const failedData = rowData.filter((item) => !item.success);

    const ws = XLSX.utils.json_to_sheet(failedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FailedData");
    XLSX.writeFile(wb, "failed_data.xlsx");
  };

  const downloadSuccessData = () => {
    const successData = rowData.filter((item) => item.success);

    const ws = XLSX.utils.json_to_sheet(successData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Success Data");
    XLSX.writeFile(wb, "Success_Data.xlsx");
  };

  const downloadReportData = () => {
    const ws = XLSX.utils.json_to_sheet(rowData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, "Report.xlsx");
  };

  return (
    <div className="email-record-page body12">
      <div className="email-record-container container123456">
        <div className="breadcrumb flat">
          <Link to="/email">Home</Link>
          <span className="breadcrumb-sep"> / </span>
          <span className="active">Email Reports (day-wise)</span>
        </div>

        <div className="email-record-controls">
          {isAdmin && (
            <div className="email-record-date-wrap">
              <label className="email-record-date-label">View as user (admin)</label>
              <select
                className="email-record-date-picker"
                value={selectedFilterUserId}
                onChange={(e) => {
                  setSelectedFilterUserId(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ padding: "8px 12px", borderRadius: "8px", minWidth: "200px" }}
              >
                <option value="">All users</option>
                {userList.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.user_name || u.user_email || `User ${u.id}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="email-record-date-wrap">
            <label className="email-record-date-label">Select date (day-wise report · latest first)</label>
            <DatePicker
              selected={selectedDate}
              onChange={handleDateChange}
              className="email-record-date-picker"
              dateFormat="dd/MM/yyyy"
            />
          </div>
          <div className="email-record-actions">
            <Button danger onClick={downloadFailedData} className="email-record-btn email-record-btn-failed">
              Download Failed Emails
            </Button>
            <Button type="primary" onClick={downloadSuccessData} className="email-record-btn email-record-btn-success">
              Download Success Emails
            </Button>
            <Button onClick={downloadReportData} className="email-record-btn email-record-btn-report">
              Download Full Report
            </Button>
          </div>
        </div>

        <div className="email-record-table-wrap">
          {rowData && rowData.length > 0 ? (
            <div className="ag-theme-quartz email-record-grid" style={{ height: "70vh", width: "100%" }}>
              <AgGridReact
                rowData={rowData}
                columnDefs={colDefs}
                pagination={false}
                domLayout="normal"
                loading={loading}
              />
            </div>
          ) : (
            <div className="email-record-empty">
              <p>No email records for this date.</p>
              <p className="email-record-empty-hint">Change the date or send some emails to see records here.</p>
            </div>
          )}
        </div>

        {rowData && rowData.length > 0 && (
          <div className="email-record-pagination">
            <button
              type="button"
              className="email-record-page-btn email-record-prev-next"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            {renderPageNumbers()}
            <button
              type="button"
              className="email-record-page-btn email-record-prev-next"
              disabled={currentPage >= totalPageCount}
              onClick={() => setCurrentPage((p) => Math.min(totalPageCount, p + 1))}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailRecord;